import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";

import Header from "./common/Header";
import LoginModal from "./common/LoginModal";

// Pages
import Home from "./pages/Home";
import CategoryPage from "./pages/CategoryPage";
import TemplateDetails from "./pages/TemplateDetails";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import SS2Editor from "./pages/InvCard";
import AboutUs from "./pages/AboutUs";
import CustomTemplateProcess from "./pages/CustomTemplateProcess";

import GeneratedVideoPage from "./pages/GeneratedVideoPage";

import { verifyTokenRequest, hydrateAuth, toggleLoginModal } from "./store/slice/authSlice";

const Footer = () => (
  <footer className="bg-gray-900 text-white py-10 mt-auto relative z-20">
    <div className="max-w-6xl mx-auto px-4 flex flex-col items-center gap-4">

      {/* Links */}
      <div className="flex gap-6 text-sm text-gray-300">
        <Link 
          to="/about-us" 
          className="hover:text-white transition"
        >
          About Us
        </Link>

        <Link 
          to="/privacy-policy" 
          className="hover:text-white transition"
        >
          Privacy Policy
        </Link>
      </div>

      {/* Copyright */}
      <p className="text-xs text-gray-400">
        © 2026 GreetIndia9. All rights reserved.
      </p>
    </div>
  </footer>
);


// Wrapper for pages
const PageLayout = ({ children }) => {
  const location = useLocation();

  const isApp =
    new URLSearchParams(location.search).get("source") === "app" ||
    window.isApp === true;

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col relative overflow-hidden">
      
      {/* Background Decorations */}
      <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] rounded-full bg-gradient-to-br from-yellow-200/40 to-transparent blur-[100px] pointer-events-none" />
      <div className="absolute -bottom-[10%] -right-[10%] w-[40%] h-[40%] rounded-full bg-gradient-to-tl from-orange-200/40 to-transparent blur-[100px] pointer-events-none" />
      <div className="absolute top-[20%] right-[-5%] w-64 h-64 bg-yellow-100/30 rounded-[3rem] rotate-45 blur-2xl pointer-events-none" />

      {/* Header only for Web */}
      {!isApp && <Header />}

      <div className="flex-1">{children}</div>

      {/* Footer only for Web */}
      {!isApp && <Footer />}
    </div>
  );
};

// --- Protected Route Component ---
// This ensures that only logged-in users can access specific pages.
const ProtectedRoute = ({ children }) => {
  const { isLoggedIn } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    // If user attempts to access a protected route without login, open the modal
    if (!isLoggedIn) {
      dispatch(toggleLoginModal(true));
    }
  }, [isLoggedIn, dispatch]);

  if (!isLoggedIn) {
    // Render a placeholder while waiting for login
    // The Global LoginModal will sit on top of this
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-gray-500">
        <p className="mb-4">Access Restricted</p>
        <p className="text-sm">Please login to view this page.</p>
      </div>
    );
  }

  return children;
};

function App() {
  const dispatch = useDispatch();

  // 1. Restore Auth on Load
  useEffect(() => {
    const savedToken = localStorage.getItem("authToken");
    const savedUser = localStorage.getItem("authUser");

    if (savedToken && savedUser) {
      dispatch(
        hydrateAuth({
          token: savedToken,
          user: JSON.parse(savedUser),
        })
      );
    }
  }, [dispatch]); // Fixed: Added dependency array to prevent infinite loops

  // 2. Handle App Token Injection (React Native)
  useEffect(() => {
    const appToken = localStorage.getItem("app_token");

    if (appToken) {
      dispatch(verifyTokenRequest({ token: appToken }));
      localStorage.removeItem("app_token");
    }
  }, [dispatch]);

  return (
    <BrowserRouter>
      <div className="flex flex-col min-h-screen relative">
        
        {/* Global Login Modal */}
        <LoginModal />

        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PageLayout><Home /></PageLayout>} />
          <Route path="/category/:slug" element={<PageLayout><CategoryPage /></PageLayout>} />
          <Route path="/template/:slug" element={<PageLayout><TemplateDetails /></PageLayout>} />
          <Route path="/about-us" element={<PageLayout><AboutUs /></PageLayout>} />
          <Route path="/privacy-policy" element={<PageLayout><PrivacyPolicy /></PageLayout>} />
          <Route path="/custom-template-process" element={<PageLayout><CustomTemplateProcess /></PageLayout>} />


          {/* Protected Routes */}
          <Route 
            path="/editor/:slug" 
            element={
              <PageLayout>
                <ProtectedRoute>
                  <SS2Editor />
                </ProtectedRoute>
              </PageLayout>
            } 
          />
          
          <Route 
            path="/generated-invitations" 
            element={
              <PageLayout>
                <ProtectedRoute>
                  <GeneratedVideoPage />
                </ProtectedRoute>
              </PageLayout>
            } 
          />

        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;